import styled from '@emotion/styled';
import Head from 'next/head';
import { useRouter } from 'next/router';

import { screen } from '@/components/common/breakpoints';
import { CaseStudySchema } from '@/components/common/CaseStudySchema';
import { constructSlug } from '@/components/common/utils';
import { BlogCaseLayout } from '@/components/layout/BlogCaseLayout';
import { Col, Row } from '@/components/layout/Grid';
import { Section } from '@/components/layout/Section';
import { PostBody } from '@/components/page/case-study/Body';
import { CaseStudyHero } from '@/components/page/case-study/CaseStudyHero';
import { CTASection } from '@/components/page/case-study/CTASection/CTASection';
import { KeyFacts } from '@/components/page/case-study/KeyFacts';
import { RelatedStories } from '@/components/page/case-study/RelatedStories';
import { Sidebar } from '@/components/page/case-study/Sidebar';
import { BASE_REVALIDATE } from '@/lib/constants';
import { useDynamicYield } from '@/lib/dynamicYield';
import {
  caseStudiesSettingsQuery,
  caseStudyQuery,
  localeTranslationsQuery,
  navigationQuery,
  navigations_exQuery,
  siteSettingsQuery,
} from '@/lib/queries';
import { getClient, readToken } from '@/lib/sanityUtils';
import { getValidLocale } from '@/lib/utils';

import type {
  SanityCaseStudiesSettingsType,
  SanityNavigationType,
} from '@/types/sanity';
import type {
  CaseStudyType,
  Navigation_exType,
  SiteSettings,
  Translation,
} from '@/types/shared';
import type { GetStaticPaths, GetStaticProps } from 'next';

export const getStaticPaths = (async () => {
  const paths = [];

  return { paths, fallback: 'blocking' };
}) satisfies GetStaticPaths;

export const getStaticProps = (async ({
  params,
  locale,
  draftMode = false,
}) => {
  const client = getClient(draftMode ? { token: readToken } : undefined);
  const slug = constructSlug(params);

  const validLocale = getValidLocale(locale);

  const [
    caseStudy,
    caseStudiesSettings,
    siteSettings,
    navigation,
    navigations_ex,
  ] = await Promise.all([
    client.fetch<CaseStudyType>(
      caseStudyQuery({
        slug: slug,
        locale: validLocale,
      }),
    ),

    client.fetch<SanityCaseStudiesSettingsType>(
      caseStudiesSettingsQuery({ locale: validLocale }),
    ),
    client.fetch<SiteSettings>(siteSettingsQuery({ locale: validLocale })),
    client.fetch<SanityNavigationType>(
      navigationQuery({ locale: validLocale }),
    ),
    client.fetch<Navigation_exType[]>(
      navigations_exQuery({ locale: validLocale }),
    ),
  ]);

  if (!caseStudy) {
    return {
      notFound: true,
    };
  }

  const translations = await client.fetch<Translation[]>(
    localeTranslationsQuery(caseStudy),
  );
  return {
    props: {
      caseStudy,
      slug,
      caseStudiesSettings,
      siteSettings,
      locale: validLocale,
      draftMode,
      navigation,
      navigations_ex,
      token: draftMode ? readToken : '',
      translations,
    },
    revalidate: Number(process.env.REVALIDATE || BASE_REVALIDATE),
  };
}) satisfies GetStaticProps;

interface CaseStudyProps {
  caseStudy: CaseStudyType;
  siteSettings: SiteSettings;
  caseStudiesSettings: SanityCaseStudiesSettingsType;
  navigation: SanityNavigationType;
  navigations_ex: Navigation_exType[];
  draftMode: boolean;
}

const CaseStudy = ({
  caseStudy,
  caseStudiesSettings,
  siteSettings,
  navigation,
  navigations_ex,
  draftMode,
}: CaseStudyProps) => {
  const { asPath, locale } = useRouter();
  const validLocale = getValidLocale(locale);
  const { recommendationContext } = useDynamicYield(validLocale, 'CATEGORY', [
    'CaseStudy',
  ]);

  const isCTASectionPresent =
    caseStudy?.ctaSection?.title?.heading ||
    caseStudy?.ctaSection?.title?.subheading;

  return (
    <BlogCaseLayout
      SEO={caseStudy?.SEO}
      siteSettings={siteSettings}
      preview={draftMode}
      navigation={navigation}
      navigations_ex={navigations_ex}
      recommendationContext={recommendationContext}
      layout={{
        spacingMode: 'margin',
        firstItemBackground: '--background-warm-base',
        lastItemBackground: '--background-warm-base',
      }}
    >
      <Head>
        <CaseStudySchema
          url={asPath}
          datePublished={caseStudy.publishDate}
          headline={caseStudy.heading}
          clientName={caseStudy?.client}
          description={caseStudy?.description}
          postBody={caseStudy?.postBody}
          image={caseStudy?.featuredImage}
        />
      </Head>
      <Section className="case-study">
        <Row>
          <Col sizes={{ lg: 6, md: 8 }} start={{ lg: 2, md: 3 }}>
            <CaseStudyHero
              heading={caseStudy?.heading}
              description={caseStudy?.description}
            />
            <KeyFactsContainer>
              <KeyFacts {...caseStudy.keyFacts} />
            </KeyFactsContainer>
            <SidebarContainerFull>
              <Sidebar {...caseStudy.sidePanel} />
            </SidebarContainerFull>
            <PostBody {...caseStudy.postBody} />
          </Col>
          <Col sizes={{ lg: 3, md: 8 }} start={{ lg: 9, md: 3 }}>
            <SidebarContainer>
              <Sidebar {...caseStudy.sidePanel} />
            </SidebarContainer>
          </Col>
        </Row>
      </Section>
      {isCTASectionPresent && (
        <CTASection
          ctaSection={caseStudy.ctaSection}
          isAudienceSwitcherEnabled={siteSettings?.isAudienceSwitcherEnabled}
        />
      )}
      {caseStudy?.relatedSection?.stories.length > 0 && (
        <RelatedStories
          relatedSection={caseStudy?.relatedSection}
          fallbackImage={caseStudiesSettings?.featuredImageDefault}
        />
      )}
    </BlogCaseLayout>
  );
};

const KeyFactsContainer = styled.div`
  display: none;

  ${screen.lg} {
    display: block;
  }
`;

const SidebarContainer = styled.div`
  position: relative;
  height: max-content;
  display: none;

  ${screen.lg} {
    position: sticky;
    top: 80px;
    margin-top: 65px;
    display: block;
  }
`;

const SidebarContainerFull = styled.div`
  height: max-content;
  display: block;

  ${screen.lg} {
    display: none;
  }
`;

export default CaseStudy;
